import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Custom date adapter that supports locale-specific date parsing
 * including dd/mm/yyyy format for non-US locales
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  /**
   * Parse date string based on locale
   * Supports both mm/dd/yyyy (US) and dd/mm/yyyy (non-US) formats
   * IMPORTANT: Only parses STRING input from users. Does NOT modify existing Date objects.
   */
  override parse(value: any): Date | null {
    if (!value) {
      return null;
    }

    // If it's already a Date object, return it unchanged
    if (value instanceof Date) {
      return value;
    }

    // Convert to string
    const dateString = String(value).trim();
    
    if (!dateString) {
      return null;
    }

    // Get the current locale
    const locale = this.locale || 'en-US';
    const languagePreference = sessionStorage.getItem('languagepreference');
    
   
    // Determine if we should use dd/mm/yyyy format
    // Use dd/mm/yyyy for most non-US locales when translation is enabled
    const useDayMonthYear = this.shouldUseDayMonthYear(locale, languagePreference);
    
    // Try to parse the date string
    const parsedDate = this.parseDate(dateString, useDayMonthYear);
    
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    // Fallback to native parsing
    return super.parse(value);
  }

  /**
   * Determine if the locale should use dd/mm/yyyy format
   */
  private shouldUseDayMonthYear(locale: string, languagePreference: string | null): boolean {
    // If no language preference or English, use US format (mm/dd/yyyy)
    if (!languagePreference || languagePreference === 'en') {
      return false;
    }

    // Locales that use dd/mm/yyyy format
    const dayMonthYearLocales = [
      'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'tr', 'cs', 'hu', 
      'ro', 'fi', 'da', 'no', 'el', 'ru', 'ar', 'hi', 'th', 'vi', 'id', 'he'
    ];

    // Locales that use yyyy/mm/dd or other formats
    const otherFormatLocales = ['ja', 'ko', 'zh'];

    const lowerLocale = locale.toLowerCase();
    const lowerLang = languagePreference.toLowerCase();

    // Check if current language preference uses dd/mm/yyyy
    if (dayMonthYearLocales.some(l => lowerLang.startsWith(l) || lowerLocale.startsWith(l))) {
      return true;
    }

    // Default to mm/dd/yyyy for US and Asian locales
    return false;
  }

  /**
   * Parse a date string in various formats
   */
  private parseDate(dateString: string, useDayMonthYear: boolean): Date | null {
    // Common separators: /, -, .
    const separatorRegex = /[\/\-\.]/;
    
    // Check if the string contains a separator
    if (!separatorRegex.test(dateString)) {
      return null;
    }

    // Split the date string
    const parts = dateString.split(separatorRegex);
    
    if (parts.length !== 3) {
      return null;
    }

    // Parse the parts as numbers
    const num1 = parseInt(parts[0], 10);
    const num2 = parseInt(parts[1], 10);
    const num3 = parseInt(parts[2], 10);

    // Check if all parts are valid numbers
    if (isNaN(num1) || isNaN(num2) || isNaN(num3)) {
      return null;
    }

    let day: number;
    let month: number;
    let year: number;

    // Determine which part is the year
    // If any part is > 31, it's likely the year
    if (num1 > 31) {
      // yyyy/mm/dd or yyyy/dd/mm format
      year = num1;
      if (useDayMonthYear) {
        day = num2;
        month = num3;
      } else {
        month = num2;
        day = num3;
      }
    } else if (num3 > 31 || num3 < 100) {
      // dd/mm/yy or mm/dd/yy format (2-digit year)
      year = num3;
      // Handle 2-digit years
      if (year < 100) {
        // Assume 20xx for years 00-50, 19xx for years 51-99
        year = year <= 50 ? 2000 + year : 1900 + year;
      }
      
      if (useDayMonthYear) {
        day = num1;
        month = num2;
      } else {
        month = num1;
        day = num2;
      }
    } else {
      // dd/mm/yyyy or mm/dd/yyyy format
      year = num3;
      
      if (useDayMonthYear) {
        day = num1;
        month = num2;
      } else {
        month = num1;
        day = num2;
      }
    }

    // Validate the ranges
    if (month < 1 || month > 12) {
      // If month is invalid, try swapping day and month
      if (!useDayMonthYear && num2 >= 1 && num2 <= 12 && num1 >= 1 && num1 <= 31) {
        // Try dd/mm/yyyy format instead
        day = num1;
        month = num2;
      } else if (useDayMonthYear && num1 >= 1 && num1 <= 12 && num2 >= 1 && num2 <= 31) {
        // Try mm/dd/yyyy format instead
        month = num1;
        day = num2;
      } else {
        return null;
      }
    }

    if (day < 1 || day > 31) {
      return null;
    }

    // Create the date (month is 0-indexed in JavaScript Date)
    const date = new Date(year, month - 1, day);

    // Verify the date is valid (accounts for invalid dates like Feb 30)
    if (date.getFullYear() !== year || 
        date.getMonth() !== month - 1 || 
        date.getDate() !== day) {
      return null;
    }

    return date;
  }

  /**
   * Format the date for display based on locale
   * IMPORTANT: This only affects what's shown in the INPUT FIELD, not the form value
   */
  override format(date: Date, displayFormat: Object): string {
    if (!this.isValid(date)) {
      return '';
    }

    const languagePreference = sessionStorage.getItem('languagepreference');
    const locale = this.locale || 'en-US';
    const useDayMonthYear = this.shouldUseDayMonthYear(locale, languagePreference);

    // Get the date parts
    const day = date.getDate();
    const month = date.getMonth() + 1; // Month is 0-indexed
    const year = date.getFullYear();

    // Pad with zeros
    const paddedDay = String(day).padStart(2, '0');
    const paddedMonth = String(month).padStart(2, '0');

    const formattedDate = useDayMonthYear 
      ? `${paddedDay}/${paddedMonth}/${year}` 
      : `${paddedMonth}/${paddedDay}/${year}`;

    // Return formatted date based on locale
    return formattedDate;
  }

  /**
   * Deserialize a value from a model to a Date object
   * This ensures the form control stores a Date object, not a string
   */
  override deserialize(value: any): Date | null {   
    if (value instanceof Date) {
      return this.isValid(value) ? value : null;
    }
    
    if (typeof value === 'string' && value) {
      // Parse the string to a Date
      return this.parse(value);
    }
    
    if (typeof value === 'number') {
      return new Date(value);
    }
    
    return null;
  }
}
